import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Bell, X, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

interface SupportMessageSummary {
  id: string;
  clientId: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
  client?: { name: string } | null;
}

export default function AdminNotificationBell() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [, setLocation] = useLocation();

  const { data: countData } = useQuery<{ count: number }>({
    queryKey: ["/api/admin/support/open-count"],
    refetchInterval: 30000,
  });

  const { data: recentMessages } = useQuery<SupportMessageSummary[]>({
    queryKey: ["/api/admin/support/recent-open"],
    enabled: open,
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const count = countData?.count || 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => setOpen(!open)}
        className="relative"
        data-testid="button-admin-notification-bell"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center"
            data-testid="badge-admin-unread-count"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </Button>

      {open && (
        <Card className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-14 sm:top-full sm:mt-2 sm:w-80 max-h-96 overflow-hidden z-50 shadow-lg">
          <CardHeader className="py-3 px-4 flex flex-row items-center justify-between gap-2 border-b">
            <CardTitle className="text-sm font-medium">Nya supportärenden</CardTitle>
            <Button size="icon" variant="ghost" onClick={() => setOpen(false)} className="h-6 w-6">
              <X className="h-3.5 w-3.5" />
            </Button>
          </CardHeader>
          <div className="overflow-y-auto max-h-72">
            {recentMessages && recentMessages.length > 0 ? (
              <div className="divide-y">
                {recentMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className="p-3 text-sm hover-elevate cursor-pointer"
                    onClick={() => {
                      setOpen(false);
                      setLocation("/admin/support");
                    }}
                    data-testid={`admin-notif-item-${msg.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="h-7 w-7 rounded-md bg-amber-500/10 flex items-center justify-center shrink-0 mt-0.5">
                        <MessageSquare className="h-3.5 w-3.5 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{msg.subject}</p>
                        <p className="text-muted-foreground text-xs mt-0.5 truncate">
                          {msg.client?.name || "Kund"} - {msg.message.length > 60 ? msg.message.substring(0, 60) + "..." : msg.message}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {format(new Date(msg.createdAt), "d MMM, HH:mm", { locale: sv })}
                        </p>
                      </div>
                      <span className="h-2 w-2 bg-amber-500 rounded-full block mt-1.5 shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <Bell className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Inga öppna ärenden</p>
              </div>
            )}
          </div>
          {count > 0 && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                className="w-full text-sm"
                onClick={() => {
                  setOpen(false);
                  setLocation("/admin/support");
                }}
                data-testid="button-view-all-support"
              >
                Visa alla ({count})
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
